/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

// store/auth/selectors.ts
import { createSelector } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useSelector } from 'react-redux';
import type { AuthStore } from './types';
import type { CoreAppStore } from '../../types';
import { selectorMemoizationOptions } from '../../utils';
import type { CommonModuleState } from '../types';

export const useAppSelector: TypedUseSelectorHook<CommonModuleState> =
  useSelector;

export const getAuthStore = (store: CoreAppStore): AuthStore => {
  if (store?.common) {
    return store.common.auth;
  }
  return null!;
};

/**
 * Gets whether the user is logged in
 *
 * @param {object} store store object from which the auth state will be extracted
 * @returns {boolean} true if user is logged in
 */
export const getIsLoggedIn = createSelector(
  getAuthStore,
  (auth): boolean => {
    return auth?.isLoggedIn ?? false;
  },
  selectorMemoizationOptions,
);

/**
 * Gets the current auth credentials
 *
 * @param {object} store store object from which the auth state will be extracted
 * @returns {Credentials | undefined} the auth credentials or undefined
 */
export const getCredentials = createSelector(
  getAuthStore,
  (auth) => {
    return auth?.credentials;
  },
  selectorMemoizationOptions,
);

/**
 * Gets the current role
 *
 * @param {object} store store object from which the auth state will be extracted
 * @returns {Role[] | undefined} the current role
 */
export const getCurrentRoles = createSelector(
  getAuthStore,
  (auth) => {
    return auth?.credentials?.roles;
  },
  selectorMemoizationOptions,
);
